/** D-175: everything the AI corrected, « à écouter » and review requests first; a trainer re-marks what they want. Course answers too. */

import {
  type CourseProduction,
  type Production,
  type ProductionStatusEnum,
  useAcademyCourseProductionsGradeCreate,
  useAcademyCourseProductionsList,
  useAcademyFormatRetrieve,
  useAcademyProductionsGradeCreate,
  useAcademyProductionsList,
} from "@agencyos/api-client";
import { Badge, type BadgeTone, Button, GlassCard, GlassSheet, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { dateTimeText } from "../billing/shared";
import { AiCorrection, GradingFilters, GradingTags, GradingWarnings, useGradingFilters } from "./GradingTools";
import { decimalInput, ErrorList, isObject, num, Pager, str, useAcademyRoles, useRefreshAcademy } from "./shared";

const STATUS_TONE: Record<ProductionStatusEnum, BadgeTone> = { draft: "default", submitted: "default", graded: "success", to_trainer: "danger" };

/** A course answer in the same sheet: same rubric, its own mark endpoint. */
function fromCourse(c: CourseProduction): Production {
  return { ...c, band: c.band ?? null, skill: c.type === "write" ? "ee" : "eo", task_number: 0, rubric: null, material: null, responses: null, turns: [], exam_level: "", needs_listening: false, review_pending: false, waiting_too_long: false } as unknown as Production;
}

export interface Criterion {
  key: string;
  max: number;
  label: unknown;
}

/** Rubrics of the exam format table (`GET /academy/format/`): nothing is hard-coded here. */
export function rubricsOf(value: unknown): Record<string, Criterion[]> {
  if (!isObject(value)) return {};
  const out: Record<string, Criterion[]> = {};
  for (const [skill, list] of Object.entries(value)) {
    if (!Array.isArray(list)) continue;
    out[skill] = (list as unknown[]).filter(isObject).map((c) => ({ key: str(c.key), max: num(c.max), label: c.label }));
  }
  return out;
}

/** Same rule as the backend: each criterion capped at its maximum, the sum rounded. */
export function gradeTotal(rubric: Criterion[], points: Record<string, string>): number {
  const sum = rubric.reduce((acc, c) => acc + Math.min(Math.max(Number(points[c.key] || 0) || 0, 0), c.max), 0);
  return Math.round(sum);
}

interface GradeRow {
  key: string;
  points: number;
  comment: string;
}

function criteriaOf(grade: unknown): GradeRow[] {
  if (!isObject(grade) || !Array.isArray(grade.criteria)) return [];
  return (grade.criteria as unknown[]).filter(isObject).map((c) => ({ key: str(c.key), points: num(c.points), comment: str(c.comment) }));
}

function GradeSheet({ production, rubric, course, onClose }: { production: Production; rubric: Criterion[]; course: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const examGrade = useAcademyProductionsGradeCreate({ mutation: { onSuccess: refresh } });
  const courseGrade = useAcademyCourseProductionsGradeCreate({ mutation: { onSuccess: refresh } });
  const grade = course ? courseGrade : examGrade;
  const [current, setCurrent] = useState<Production>(production);
  const previous = criteriaOf(current.grade);
  const info = isObject(current.grade) ? current.grade : {};
  const [points, setPoints] = useState<Record<string, string>>(() =>
    Object.fromEntries(rubric.map((c) => [c.key, String(previous.find((p) => p.key === c.key)?.points ?? "")])),
  );
  const [comments, setComments] = useState<Record<string, string>>(() =>
    Object.fromEntries(rubric.map((c) => [c.key, previous.find((p) => p.key === c.key)?.comment ?? ""])),
  );
  const [comment, setComment] = useState(str(info.comment));
  const errors = fieldErrors(grade.error);
  const total = gradeTotal(rubric, points);
  const complete = rubric.length > 0 && rubric.every((c) => points[c.key] !== "" && points[c.key] !== undefined);
  const source = str(info.source);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    grade.mutate(
      {
        id: current.id,
        data: {
          criteria: rubric.map((c) => ({ key: c.key, points: Math.min(Math.max(Number(points[c.key]) || 0, 0), c.max), comment: comments[c.key] ?? "" })),
          comment,
        },
      },
      { onSuccess: (r) => r.status === 200 && setCurrent(course ? fromCourse(r.data as CourseProduction) : (r.data as Production)) },
    );
  };

  return (
    <GlassSheet open title={t("academyAdmin.grading.sheetTitle", { skill: current.skill.toUpperCase(), n: current.task_number })} onClose={onClose}>
      <div className="stack">
        <div className="row">
          <Badge tone={STATUS_TONE[current.status]}>{t(`academyAdmin.grading.status.${current.status}`)}</Badge>
          <span className="muted">{current.learner_email}</span>
          <span className="muted">{dateTimeText(current.created_at, i18n.language)}</span>
        </div>
        <section className="installment-row stack">
          <strong>{t("academyAdmin.grading.prompt")}</strong>
          <p lang="fr" className="academy-pre">
            {current.prompt}
          </p>
        </section>
        <section className="stack">
          <strong>{current.skill === "eo" ? t("academyAdmin.grading.recording") : t("academyAdmin.grading.text")}</strong>
          {current.audio_url ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- its transcript is shown just below as text
            <audio controls preload="none" src={current.audio_url} aria-label={t("academyAdmin.grading.recording")} />
          ) : null}
          {current.text ? (
            <p lang="fr" className="installment-row academy-pre">
              {current.text}
            </p>
          ) : (
            <p className="muted">{t("academyAdmin.grading.noText")}</p>
          )}
          {current.skill === "ee" ? <span className="muted">{t("academyAdmin.grading.words", { count: current.word_count })}</span> : null}
          {str(info.word_warning) ? <p className="notice-warning installment-row">{str(info.word_warning)}</p> : null}
        </section>
        {current.score !== null ? (
          <section className="installment-row stack">
            <div className="row">
              <strong>{t("academyAdmin.grading.currentScore", { score: current.score })}</strong>
              <Badge tone={source === "ai" ? "offer" : "success"}>{source === "ai" ? t("academyAdmin.grading.byAi") : t("academyAdmin.grading.byTrainer")}</Badge>
              {typeof info.ai_score === "number" ? <span className="muted">{t("academyAdmin.grading.aiScore", { score: info.ai_score })}</span> : null}
            </div>
            <p className="muted">{t("academyAdmin.grading.estimate")}</p>
          </section>
        ) : null}

        <AiCorrection grade={current.grade} />
        <form className="stack" onSubmit={onSubmit} noValidate>
          <h3>{t("academyAdmin.grading.form")}</h3>
          {rubric.length ? null : <p className="ko">{t("academyAdmin.grading.noRubric")}</p>}
          <fieldset className="plain-fieldset stack" disabled={!canEdit}>
            {rubric.map((c) => {
              const ai = previous.find((p) => p.key === c.key);
              return (
                <div key={c.key} className="installment-row stack">
                  <div className="form-grid">
                    <TextField
                      label={t("academyAdmin.grading.criterion", { label: localized(c.label, i18n.language) || c.key, max: c.max })}
                      inputMode="decimal"
                      value={points[c.key] ?? ""}
                      hint={ai && source === "ai" ? t("academyAdmin.grading.aiGave", { points: ai.points }) : undefined}
                      onChange={(e) => {
                        const value = decimalInput(e.target.value);
                        setPoints((all) => ({ ...all, [c.key]: value !== "" && Number(value) > c.max ? String(c.max) : value }));
                      }}
                    />
                  </div>
                  <TextAreaField
                    label={t("academyAdmin.grading.criterionComment")}
                    rows={2}
                    value={comments[c.key] ?? ""}
                    onChange={(e) => setComments((all) => ({ ...all, [c.key]: e.target.value }))}
                  />
                </div>
              );
            })}
            <TextAreaField label={t("academyAdmin.grading.comment")} rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
            <p className="big-number" aria-live="polite">
              {t("academyAdmin.grading.total", { total })}
            </p>
            {errors.criteria ? (
              <p role="alert" className="ao-field__error">
                {errors.criteria}
              </p>
            ) : (
              <ErrorList error={grade.error} />
            )}
            <div className="row">
              <Button type="submit" disabled={grade.isPending || !complete}>
                {grade.isPending ? t("common.loading") : t("academyAdmin.grading.grade")}
              </Button>
              {grade.isSuccess ? (
                <span role="status" className="ok">
                  {t("academyAdmin.grading.graded")}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
    </GlassSheet>
  );
}

export function GradingTab() {
  const { t, i18n } = useTranslation();
  const grading = useGradingFilters();
  const [kind, setKind] = useState<"exam" | "course">("exam");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Production | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const format = useAcademyFormatRetrieve();
  const rubrics = useMemo(() => rubricsOf(format.data?.status === 200 ? format.data.data.rubrics : undefined), [format.data]);
  // D-123: the TCF copies only (the IELTS, TEF and German copies have their own tab and grid)
  const filters = { ...grading.params, page, exam: "tcf_canada" as const };
  const list = useAcademyProductionsList(filters, { query: { enabled: kind === "exam" } });
  const courseList = useAcademyCourseProductionsList(filters, { query: { enabled: kind === "course" } });
  const examData = list.data?.status === 200 ? list.data.data : undefined;
  const courseData = courseList.data?.status === 200 ? courseList.data.data : undefined;
  const data = kind === "exam" ? examData : courseData && { ...courseData, results: courseData.results.map(fromCourse) };

  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academyAdmin.grading.title")}</h2>
          <p className="muted">{t("academyAdmin.grading.intro")}</p>
        </div>
      </div>
      <GradingWarnings />
      <SelectField
        label={t("academyAdmin.ielts.grading.source")}
        value={kind}
        options={(["exam", "course"] as const).map((s) => ({ value: s, label: t(`academyAdmin.ielts.grading.sources.${s}`) }))}
        onChange={(e) => {
          setKind(e.target.value as "exam" | "course");
          setPage(1);
        }}
      />
      <GradingFilters
        values={grading.values}
        course={kind === "course"}
        onChange={(next) => {
          grading.setValues(next);
          setPage(1);
        }}
      />
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.results.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.grading.learner")}</th>
                  <th>{t("academyAdmin.grading.task")}</th>
                  <th>{t("academyAdmin.grading.date")}</th>
                  <th>{t("academyAdmin.grading.wordCount")}</th>
                  <th>{t("academyAdmin.grading.score")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((p) => {
                  const source = isObject(p.grade) ? str(p.grade.source) : "";
                  return (
                    <tr key={p.id}>
                      <td>
                        <button type="button" className="link" onClick={() => setOpen(p)}>
                          {p.learner_email}
                        </button>
                      </td>
                      <td className="nowrap">
                        {p.skill.toUpperCase()} · {t("academyAdmin.topics.task", { n: p.task_number })}
                      </td>
                      <td className="nowrap">{dateTimeText(p.created_at, i18n.language)}</td>
                      <td>{p.skill === "ee" ? p.word_count : "—"}</td>
                      <td className="nowrap">
                        {p.score === null ? "—" : `${p.score} / 20`}
                        {source ? <span className="muted"> · {source === "ai" ? t("academyAdmin.grading.byAi") : t("academyAdmin.grading.byTrainer")}</span> : null}
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[p.status]}>{t(`academyAdmin.grading.status.${p.status}`)}</Badge> <GradingTags row={p} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <p className="muted">{t(`academyAdmin.grading.empty.${grading.values.status}`)}</p>
      )}
      {open ? <GradeSheet key={open.id} production={open} rubric={rubrics[open.skill] ?? []} course={kind === "course"} onClose={close} /> : null}
    </GlassCard>
  );
}
